// Gestione consenso cookie (GDPR) + caricamento tag condizionato.
// Nessuno script non essenziale viene caricato prima del consenso.
// Consent Mode v2: default denied a tutte le categorie, update alla scelta dell'utente.

import { getTagConfig } from "./analytics";

const KEY = "flash_cookie_consent";
const CONSENT_VERSION = "2026-09";

const gaDebug = (...args) => {
  if (typeof window === "undefined") return;
  if (window.location.hostname === "localhost" || window.location.search.includes("ga_debug=1")) {
    console.log(...args);
  }
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  // Firma volutamente identica allo snippet ufficiale gtag.js: Arguments object, non Array.
  // Un arrow con rest params pusherebbe un Array e il container Google può non processare i comandi config/get.
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
};

export const setConsent = (consent) => {
  localStorage.setItem(KEY, JSON.stringify({ ...consent, version: CONSENT_VERSION, at: new Date().toISOString() }));
  applyConsent(consent);
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent("flash:open-cookie-prefs"));
};

// Consent Mode v2: default "denied" una sola volta all'avvio; le scelte successive sono "update"
let consentDefaultPushed = false;
export const applyConsent = (consent) => {
  if (typeof window === "undefined") return;
  ensureGtag();
  const state = {
    analytics_storage: consent?.analytics ? "granted" : "denied",
    ad_storage: consent?.marketing ? "granted" : "denied",
    ad_user_data: consent?.marketing ? "granted" : "denied",
    ad_personalization: consent?.marketing ? "granted" : "denied",
  };
  if (!consentDefaultPushed) {
    // Default SEMPRE denied, anche per utenti di ritorno: la scelta salvata arriva subito dopo come update.
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    consentDefaultPushed = true;
    gaDebug("[GA4] consent default denied");
    if (consent?.analytics || consent?.marketing) {
      window.gtag("consent", "update", state);
      if (consent?.analytics) gaDebug("[GA4] analytics consent granted");
    }
  } else {
    window.gtag("consent", "update", state);
    if (consent?.analytics) gaDebug("[GA4] analytics consent granted");
  }
  if (consent?.analytics || consent?.marketing) loadTags();
  if (consent?.marketing) maybeConfigAds();
};

// Architettura tag centralizzata: GTM oppure gtag.js (GA4 diretto), mai entrambi per GA4.
let tagsLoaded = false;
export const loadTags = async () => {
  if (tagsLoaded) return;
  tagsLoaded = true;
  const cfg = await getTagConfig();
  // Measurement ID solo sul dominio di produzione: anteprima/staging non inquinano GA4
  try {
    const prodHost = cfg.siteUrl ? new URL(cfg.siteUrl).hostname : "";
    const host = window.location.hostname;
    if (prodHost && host !== prodHost && host !== `www.${prodHost}`) return;
  } catch { /* siteUrl non valido: non caricare */ return; }
  if (cfg.gtmContainerId) {
    // GTM gestisce GA4 e Google Ads al suo interno: nessun doppio caricamento
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${cfg.gtmContainerId}`;
    document.head.appendChild(s);
    return;
  }
  const primary = cfg.ga4MeasurementId || cfg.googleAdsId;
  if (!primary) return; // nessun ID configurato: nessuno script
  ensureGtag();
  window.__ga4Direct = Boolean(cfg.ga4MeasurementId);
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  s.onload = () => {
    window.__ga4Loaded = true;
    gaDebug("[GA4] initialized");
    window.gtag("js", new Date());
    if (cfg.ga4MeasurementId) {
      window.gtag("config", cfg.ga4MeasurementId, { send_page_view: true });
    }
    maybeConfigAds(cfg);
  };
  document.head.appendChild(s);
};

// Google Ads si attiva SOLO con ID configurato + consenso marketing esplicito
export const maybeConfigAds = async (cfg = null) => {
  cfg = cfg || (await getTagConfig());
  if (!cfg.googleAdsId || window.__adsConfigured) return;
  if (!getConsent()?.marketing) return;
  if (typeof window.gtag !== "function") return;
  window.__adsConfigured = true;
  window.gtag("config", cfg.googleAdsId);
};

// All'avvio: imposta consent denied di default; se consenso già dato, carica i tag
export const initConsent = () => {
  const existing = getConsent();
  applyConsent(existing || { necessary: true, analytics: false, marketing: false });
};
